import { Component, EventEmitter, Input, Output } from '@angular/core';
import { PublicationConfiguration } from '@models/PublicationConfiguration';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors, ValidatorFn, FormControl, FormArray } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { FontSelectorComponent } from '../font-selector/font-selector.component';
import { ProjectTextStyleDirective } from '../../project-text-style.directive';
import { POLYGLOSSIA_LANGUAGES } from '@models/polyglossia-languages';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent, ConfirmationDialogData } from 'src/app/confirmation-dialog/confirmation-dialog.component';
import { PublicationConfigurationRow } from '@models/PublicationConfigurationRow';
import { Canon } from '@models/Canon';

@Component({
  selector: 'app-publication-configuration',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatCheckboxModule,
    MatCardModule,
    MatExpansionModule,
    FontSelectorComponent,
    ProjectTextStyleDirective,
    MatIconModule,
    MatButtonModule
  ],
  templateUrl: './publication-configuration.component.html',
  styleUrl: './publication-configuration.component.scss'
})
export class PublicationConfigurationComponent {
  @Input() control!: FormControl;
  @Input() resetEvent!: EventEmitter<void>;
  @Input() parsingFormatGroup!: FormGroup;
  @Output() deleteConfigurationEvent = new EventEmitter<string>();

  group: FormGroup = this.formBuilder.group({});
  customThresholdsGroup: FormGroup = this.formBuilder.group({});

  useSblBibLit: boolean = true;

  constructor(private formBuilder: FormBuilder, public dialog: MatDialog) {
  }

  ngOnInit() {
    const configuration = this.control.value as PublicationConfiguration;

    this.group.addControl('footnoteMarkers', new FormControl('', Validators.required));
    this.group.addControl('polyglossiaOtherLanguage', new FormControl('', Validators.required));
    this.group.addControl('chapterHeader', new FormControl('', [Validators.required, needsChapterValidator()]));
    this.group.addControl('publicationProjectFont', new FormControl('', Validators.required));
    this.group.addControl('publicationBiblicalFont', new FormControl('', Validators.required));
    this.group.addControl('latex_template', new FormControl('', Validators.required));
    this.group.addControl('css_template', new FormControl(''));
    this.group.addControl('footnote_style', new FormControl('lettered-by-verse', Validators.required));
    this.group.addControl('useCustomFrequencyThresholds', new FormControl(false));


    /// add controls for the parsing formats
    const parsingFormatsInitialValues: { [key: string]: string | undefined } = {};
    for (const c of configuration.project.canons) {
      this.group.addControl(c, new FormControl(undefined, Validators.required));
      parsingFormatsInitialValues[c] = configuration.parsing_formats.get(c);
    }

    const usingCustomThresholds = configuration.frequency_thresholds !== undefined;
    for (const c of configuration.project.canons) {
      if (usingCustomThresholds) {
        this.customThresholdsGroup.addControl(c, new FormControl({ value: configuration.frequency_thresholds.get(c), disabled: true }));
      } else {
        this.customThresholdsGroup.addControl(c, new FormControl({ value: configuration.project.getFrequencyThreshold(c), disabled: true }));
      }
    }
    this.group.addControl('customThresholdsGroup', this.customThresholdsGroup);

    /// enable/disable the custom thresholds group based on the checkbox
    this.group.get('useCustomFrequencyThresholds')?.valueChanges.subscribe((value) => {
      if (value) {
        this.customThresholdsGroup.enable();
      } else {
        this.customThresholdsGroup.disable();
      }
    });

    this.group.patchValue({
      footnoteMarkers: configuration.footnoteMarkers ? configuration.footnoteMarkers.join(' ') : "",
      polyglossiaOtherLanguage: configuration.polyglossiaOtherLanguage,
      chapterHeader: configuration.chapterHeader,
      publicationProjectFont: configuration.publicationProjectFont,
      publicationBiblicalFont: configuration.publicationBiblicalFont,
      latex_template: configuration.latex_template,
      css_template: configuration.css_template,
      footnote_style: configuration.footnote_style,
      useCustomFrequencyThresholds: configuration.frequency_thresholds !== undefined,
      ...parsingFormatsInitialValues
    });




    this.control.setValidators(() => this.group.valid ? null : { groupInvalid: true });

    this.group.valueChanges.subscribe((values) => {
      const parsingFormatSettings: Partial<Record<Canon, string>> = {};
      this.control.value.project.canons.forEach((c: Canon) => {
        parsingFormatSettings[c] = values[c];
      });

      const customThresholds = this.group.get('useCustomFrequencyThresholds')?.value || false as boolean;
      const thresholds: Record<Canon, number> = {} as Record<Canon, number>;
      if (customThresholds) {
        Object.keys(this.customThresholdsGroup.controls).map(key => {
          thresholds[key as Canon] = this.customThresholdsGroup.get(key)?.value;
        });
      }

      const row: PublicationConfigurationRow = {
        footnoteMarkers: values.footnoteMarkers.split(' ').filter((marker: string) => marker.trim() !== ''),
        polyglossiaOtherLanguage: values.polyglossiaOtherLanguage,
        chapterHeader: values.chapterHeader,
        publication_project_font: values.publicationProjectFont,
        publication_biblical_font: this.useSblBibLit ? 'SBL BibLit' : values.publicationBiblicalFont,
        latex_template: values.latex_template,
        css_template: values.css_template,
        footnote_style: values.footnote_style,
        parsing_formats: parsingFormatSettings,
        frequency_thresholds: customThresholds ? thresholds : undefined,
      };

      this.control.setValue(PublicationConfiguration.fromRow(row, this.control.value.id, this.control.value.project), { emitEvent: false });
    });
  }

  get project() {
    return this.control.value.project;
  }

  get noParsingFormats() {
    return Object.keys(this.parsingFormatGroup.controls).length === 0;
  }

  get parsingFormatCanons(): string[] {
    return Object.keys(this.parsingFormatGroup.controls);
  }

  getParsingFormatArray(canon: string): FormArray {
    return this.parsingFormatGroup.get(canon) as FormArray;
  }

  get chapterHeader() {
    return this.group.get('chapterHeader');
  }

  get footnoteMarkers() {
    return this.group.get('footnoteMarkers');
  }

  demoChapterHeader(number: string) {
    return this.chapterHeader?.value.replace('__CHAPTER__', this.control.value.project.replaceNumerals(number));
  }

  demoFootnoteMarkers(howmany: number) {
    const markers = this.footnoteMarkers?.value.split(' ').filter((marker: string) => marker.trim() !== '');
    let result = "";
    for (let i = 0; i < howmany; i++) {
      result += markers[i % markers.length] + ' ';
    }
    return result;
  }

  get polyglossia_languages(): string[] {
    return POLYGLOSSIA_LANGUAGES;
  }

  save() {
    console.error("Save not implemented");
  }

  resetLatexTemplate() {
    if (this.group.get('latex_template')?.value === PublicationConfiguration.default_latex_template) {
      return;
    }

    const data: ConfirmationDialogData = {
      title: 'Reset LaTeX Template?',
      message: 'Do you really want to reset to the default LaTeX template and lose your edits?',
      yes: 'Yes, set to default',
      no: 'No, keep my edits',
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '350px',
      data: data,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.group.patchValue({
          latex_template: PublicationConfiguration.default_latex_template
        });
      }
    });
  }

  resetCSSTemplate() {
    if (this.group.get('css_template')?.value === PublicationConfiguration.default_css_template) {
      return;
    }

    const data: ConfirmationDialogData = {
      title: 'Reset CSS Template?',
      message: 'Do you really want to clear your custom CSS code?',
      yes: 'Yes, clear',
      no: 'No, keep my edits',
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '350px',
      data: data,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.group.patchValue({
          css_template: PublicationConfiguration.default_css_template
        });
      }
    });
  }

  deleteConfiguration() {

    const data: ConfirmationDialogData = {
      title: `Delete configuration ${this.control.value.id}?`,
      message: `Do you really want to delete the publication configuration “${this.control.value.id}”?`,
      yes: 'Yes, delete',
      no: 'No, cancel',
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '350px',
      data: data,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.deleteConfigurationEvent.emit(this.control.value.id);
      }
    });
  }


}


function needsChapterValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const chapterHeader = control.value || "";
    return chapterHeader.indexOf('__CHAPTER__') != -1 ? null : { needsChapter: true };
  };
}
